// The withdrawal hold: while a withdrawal request is open, no automated message reaches the patient.
// Pure (no imports, no Node built-ins, ASCII): the back office imports it through logic/withdrawalRequest.ts,
// and `node scripts/sync-withdrawal-pause.mjs --apply` splices the region into every gate node.
// EDIT HERE, then re-run the sync. S-182 compares every copy and executes the gates.
// Spec: docs/specs/2026-10-26-withdrawal-request-contact-hold.md

// WITHDRAWAL-HOLD-START
const WH_TEMPLATE = 'cc_unalign_after_lockin';
const WH_OPEN_STATUSES = ['pending', 'scheduled', 'in_progress', 'overdue'];
const WH_PERIOD_STATUSES = ['active', 'pending_unalignment', 'unaligned'];
const WH_CLOSED_STATUSES = ['cancelled', 'completed'];
// The evidence WF67 writes when it closes a request (its cancel and close lanes).
const WH_CLOSED_BY = ['wf67:cancel_request', 'wf67:withdrawn'];
// The read every gate makes; WF50 forwards these filters and apiv2 splits the status list on commas.
const WH_QUERY = { template_code: WH_TEMPLATE, status: WH_OPEN_STATUSES.join(','), assigned_to_type: 'staff' };
function whStr(v) { return v == null ? '' : String(v); }
function whUid(row) { return whStr(row.withings_user_id != null ? row.withings_user_id : row.id_dev_user); }
function whPeriod(row) { return whStr(row.id_care_period != null ? row.id_care_period : row.care_period_id); }
// Every field of the row WF67 writes but its status, so a row made any other way holds nobody.
function whIsRequest(row) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return false;
  const cp = whPeriod(row);
  const vis = row.visible_to_patient;
  const key = whStr(row.dedupe_key);
  const prefix = WH_TEMPLATE + ':' + cp + ':r';
  return whStr(row.template_code) === WH_TEMPLATE
    && whStr(row.assigned_to_type) === 'staff'
    && whStr(row.owner_role) === 'care_coordinator'
    && (vis === false || vis === 'false' || vis === 0 || vis === '0' || vis === 'f')
    && whStr(row.trigger_source) === 'care_team_manual'
    && whStr(row.trigger_ref_type) === 'care_period'
    && /^\d+$/.test(cp) && whStr(row.trigger_ref_id) === cp
    && whUid(row) !== ''
    && key.indexOf(prefix) === 0 && /^\d+$/.test(key.slice(prefix.length));
}
function isPausingRequest(row) { return whIsRequest(row) && WH_OPEN_STATUSES.includes(whStr(row.status)); }
// A WF50 envelope -> the held patients. Unknown when the read failed or filled its page: a gate then
// decides its own way (a message fails closed, the crisis text fails open). `periods` (optional) maps a
// care period id to its status; a request on any other period, or on a closed one, holds nobody.
function whHeld(env, opts) {
  const o = opts || {};
  const limit = Number(o.limit) > 0 ? Number(o.limit) : 0;
  if (!env || typeof env !== 'object' || env.status !== 'success' || !Array.isArray(env.data)) return { known: false, uids: [] };
  if (limit && env.data.length >= limit) return { known: false, uids: [] };
  const uids = [];
  for (const row of env.data) {
    if (!isPausingRequest(row)) continue;
    if (o.periods && !WH_PERIOD_STATUSES.includes(whStr(o.periods[whPeriod(row)]))) continue;
    const uid = whUid(row);
    if (!uids.includes(uid)) uids.push(uid);
  }
  return { known: true, uids };
}
// A WF50 envelope of requests of every status -> when each patient's last closed request ended (epoch ms), so a
// missed-reading scan counts a gap from the end of a hold. Only a request WF67 closed counts, at the earliest of
// completed_at and modified, never in the future nor before the request was created, so a later write can move the
// end no further than its own time (an update through WF50's ?__id= door still can, see the spec's risks). `periods` (optional)
// keeps only requests on those care periods (another program's close moves nothing). Unknown as whHeld. `nowMs`
// (optional) is for tests.
function whHoldEnds(env, opts) {
  const o = opts || {};
  const limit = Number(o.limit) > 0 ? Number(o.limit) : 0;
  const now = Number(o.nowMs) > 0 ? Number(o.nowMs) : Date.now();
  if (!env || typeof env !== 'object' || env.status !== 'success' || !Array.isArray(env.data)) return { known: false, ends: {} };
  if (limit && env.data.length >= limit) return { known: false, ends: {} };
  const ends = {};
  for (const row of env.data) {
    if (!whIsRequest(row) || !WH_CLOSED_STATUSES.includes(whStr(row.status))) continue;
    if (WH_CLOSED_BY.indexOf(whStr(row.completion_evidence_ref)) < 0) continue;
    if (o.periods && !Object.prototype.hasOwnProperty.call(o.periods, whPeriod(row))) continue;
    const times = [Date.parse(whStr(row.completed_at)), Date.parse(whStr(row.modified))].filter((x) => Number.isFinite(x));
    if (!times.length) continue;
    const t = Math.min(Math.min.apply(null, times), now);
    const created = Date.parse(whStr(row.created != null ? row.created : row.created_at));
    if (!Number.isFinite(created) || t < created) continue;
    const uid = whUid(row);
    if (!Object.prototype.hasOwnProperty.call(ends, uid) || t > ends[uid]) ends[uid] = t;
  }
  return { known: true, ends };
}
// WITHDRAWAL-HOLD-END

export { WH_TEMPLATE, WH_OPEN_STATUSES, WH_PERIOD_STATUSES, WH_CLOSED_STATUSES, WH_CLOSED_BY, WH_QUERY, isPausingRequest, whHeld, whHoldEnds };
